import { TaxonomyFacet } from '@edgebox/sync-core-data-definitions';
import { useEffect, useMemo, useState } from 'react';
import { getCachedTaxonomyTermsByFacet, ITaxonomyTermFacetSource } from '../Onboarding/ContentInventory/Components/useCachedTaxonomyData';
import {
  AuthoritySourceIndex,
  AuthoritySourceTermLike,
  buildAuthoritySourceIndex,
  EMPTY_AUTHORITY_SOURCE_INDEX,
} from './citationSources.utils';

/** The curated-source lookup, plus whether it is worth reading yet. */
export interface AuthoritySourceResolution {
  index: AuthoritySourceIndex;
  /**
   * False until the terms have arrived.
   *
   * Callers must skeleton their labels while this is false rather than render the hostname: the
   * hostname is real data, but showing it and then swapping in the curated name a moment later
   * makes the same source read two different ways within one page load — the exact drift this
   * whole module exists to prevent.
   */
  ready: boolean;
}

/**
 * The `authority-source` terms, indexed by every hostname they answer to.
 *
 * Reads through the shared per-facet cache, so the three surfaces that need it — list chips,
 * answer detail, the Analytics Sources module — cost one request between them for the session.
 * Archived terms are excluded at the fetch, so a retired curation simply stops supplying a name
 * and the source falls back to its hostname; it is never able to hide a measurement.
 */
export function useAuthoritySources(api?: ITaxonomyTermFacetSource): AuthoritySourceResolution {
  const [terms, setTerms] = useState<AuthoritySourceTermLike[] | undefined>(undefined);

  useEffect(() => {
    if (!api) {
      return;
    }

    let active = true;
    void getCachedTaxonomyTermsByFacet(api, TaxonomyFacet.AuthoritySource)
      .then((loaded) => {
        if (active) {
          setTerms(loaded as unknown as AuthoritySourceTermLike[]);
        }
      })
      .catch(() => {
        // A project that tracks no authority sources at all answers the same way a failure does,
        // and both mean the same thing to the reader: every source shows as its hostname. Settling
        // on an empty list keeps the surfaces out of a permanent skeleton either way.
        if (active) {
          setTerms([]);
        }
      });

    return () => {
      active = false;
    };
  }, [api]);

  const index = useMemo(() => (terms ? buildAuthoritySourceIndex(terms) : EMPTY_AUTHORITY_SOURCE_INDEX), [terms]);

  return { index, ready: terms !== undefined };
}
